/**
 * What the Transfer Portal tab decides, kept out of the components that show it.
 *
 * `npm test` here is bare `node --experimental-strip-types --test` over
 * `tests/` with no framework behind it, so anything worth asserting has to be a
 * plain function free of React and of Vite. These four are the ones worth it:
 * what the Add form sends, what it suggests calling a profile, what the list
 * says about a profile, and where the file a Run published is fetched from.
 */

import type {
  RunSummary,
  TemplateParameter,
  TemplateValues,
  TransferPortalComposition,
} from "./api";

/**
 * The body the Add form posts, given a composition and the one typed field.
 *
 * **The composition's defaults go through untouched and the name is the only
 * thing added.** That is the contract rather than a convenience: the League,
 * the sport, and both addresses are composed on the server from a sport and a
 * season, and a form that rebuilt any of them here would be a form that could
 * disagree with the composer. The `sportKey` and the `yearSport` a publisher
 * wants never reach this file, which is the whole point of a template.
 *
 * `template_key` and `template_parameters` travel with it so that the saved
 * profile records which recipe made it and what it was given — the same reason
 * a Source Profile keeps them.
 */
export function transferPortalCreateBody(
  composition: TransferPortalComposition,
  name: string,
): Record<string, unknown> {
  return {
    ...composition.defaults,
    name: name.trim(),
    template_key: composition.template_key,
    template_parameters: composition.parameters,
  };
}

export interface RunVerdict {
  /** What the row says. */
  label: string;
  /** Which `StatusBadge` treatment it gets, or null for no badge. */
  status: string | null;
  /** True while the Run is still moving, so the list keeps polling. */
  live: boolean;
}

/**
 * What a profile's last Run says about it, for the list.
 *
 * **Never run and failed are different answers, and a list that showed a dash
 * for both would be the thing this tab exists to replace.** The client's
 * women's pipeline published header-only files for four months because nothing
 * anywhere distinguished "it ran and produced nothing" from "nobody ran it".
 *
 * A successful Run says how many players it published rather than only that it
 * succeeded, because the number is what a falling match rate shows up in — and
 * "succeeded" beside an empty file is exactly the report that hid the problem.
 */
export function lastRunVerdict(run: RunSummary | null | undefined): RunVerdict {
  if (!run) {
    return { label: "Never run", status: null, live: false };
  }
  if (run.status === "queued" || run.status === "running") {
    return {
      label: run.phase ? `Running — ${run.phase}` : "Queued",
      status: run.status,
      live: true,
    };
  }
  if (run.status === "succeeded") {
    const players = run.player_count ?? 0;
    return {
      label: `${players} player${players === 1 ? "" : "s"} published`,
      status: "succeeded",
      live: false,
    };
  }
  if (run.status === "failed") {
    return { label: run.failure_code ?? "Failed", status: "failed", live: false };
  }
  return { label: run.status, status: run.status, live: false };
}

/**
 * Where the file a Run published is fetched from.
 *
 * Through the portal rather than from object storage, so that checking a Run's
 * output does not need an AWS console — and so that a destination repointed
 * since the Run says so instead of answering 404. The filename is the Run's own
 * `available_downloads` entry, which is the only name the endpoint accepts.
 */
export function runDownloadUrl(runId: string, filename: string): string {
  return `/api/v1/runs/${runId}/downloads/${encodeURIComponent(filename)}`;
}

/**
 * What the Add form suggests calling a profile, given the sport chosen.
 *
 * **It is derived rather than written down, which it was not until football.**
 * The placeholder read `247Sports — men's basketball` as a constant, which was
 * true for exactly as long as men's basketball was the only sport 247Sports
 * offered. It now offers three, and two of them are the same sport in different
 * Leagues — so a constant would suggest the wrong name to every football
 * operator, and `Football (FBS)` against `Football (FCS)` is the one place where
 * the suggested name is the only thing on screen telling two profiles apart.
 *
 * The label comes from the parameter's own options, so a publisher that adds a
 * sport needs nothing here. A sport parameter that is missing, or a value with
 * no option declaring it, falls back to the publisher alone rather than to a
 * guess — a placeholder is a suggestion, and a wrong suggestion is worse than a
 * short one.
 */
export function transferPortalNamePlaceholder(
  publisher: string,
  parameters: TemplateParameter[],
  values: TemplateValues,
): string {
  const sport = parameters.find((parameter) => parameter.key === "sport");
  const chosen = sport?.options.find((option) => option.value === values.sport);
  return chosen ? `${publisher} — ${chosen.label}` : publisher;
}

/**
 * What the Add form says about the credential this publisher wants.
 *
 * **It was a constant, and the constant became wrong the moment there were two
 * publishers.** It read "The Run reads an anonymous guest token out of the page
 * above as its first action" — true of 247Sports, and of On3 a description of a
 * request that is never made against a page that does not exist. An operator
 * reading it would go looking for the token page in the summary above and find
 * a dash.
 *
 * Derived from the composed `page_url`, because that is the field the
 * difference actually lives in: a publisher that needs a Source Session
 * composes the page it is read out of, and a publisher that needs nothing
 * composes null. So this follows the server rather than keeping a list of
 * publishers in the frontend — which is the property the whole tab is written
 * for, and the reason adding On3 cost the components nothing else.
 */
export function transferPortalCredentialNote(
  defaults: Record<string, unknown>,
): string {
  const page = defaults.page_url;
  if (typeof page === "string" && page.trim() !== "") {
    return (
      "This publisher needs no account. The Run reads an anonymous guest token out of " +
      "the page above as its first action, and keeps none of it between Runs."
    );
  }
  return (
    "This publisher needs no credential of any kind — no account, no token and no " +
    "cookie — so the Run presents nothing and acquires nothing."
  );
}

/**
 * What a profile's next scheduled firing is, for the list.
 *
 * Three answers rather than a timestamp or a dash, because a dash would
 * conflate the two cases that matter. **A profile nobody has scheduled is not
 * broken** — the portal season has deadlines an operator refreshes the sheet
 * ahead of by hand, and a list that drew those identically to a schedule that
 * is switched off would send somebody to fix the wrong one.
 *
 * And a cron written down but not switched on is its own state: the expression
 * is saved, the next firing can be computed from it, and nothing will happen.
 * That is the state an operator leaves a profile in while deciding, and the
 * one that is dangerous to show as "scheduled".
 *
 * **This is the half a timer makes invisible.** A profile that last ran three
 * days ago is fine if it runs weekly and broken if it was due this morning,
 * and the last Run alone cannot tell those apart — which is the whole reason
 * the issue asks for this column beside the other two rather than instead of
 * them.
 */
export type NextFiring =
  | { kind: "scheduled"; at: string }
  | { kind: "configured"; at: string | null }
  | { kind: "manual" };

export function nextFiring(profile: {
  next_run_at: string | null;
  schedule_enabled: boolean;
}): NextFiring {
  if (profile.schedule_enabled && profile.next_run_at) {
    return { kind: "scheduled", at: profile.next_run_at };
  }
  if (profile.next_run_at) {
    return { kind: "configured", at: profile.next_run_at };
  }
  // A schedule switched on whose cron has no reachable firing — 30 February —
  // lands here rather than in `scheduled`, which is correct: there is nothing
  // to show, and claiming a next Run there is not one would be worse than
  // saying the profile is not on a timer.
  return { kind: "manual" };
}
